import { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Modal } from '@shared/ui/Modal';
import { Button } from '@shared/ui/Button';
import { Field, Input, Label } from '@shared/ui/Input';
import {
  useAnalyzeContractMutation,
  useConfirmAuditSessionMutation,
  useGetAuditPackageQuery,
  useListAuditSessionsQuery,
  useStartAuditSessionMutation,
} from '@shared/api';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import { analysisStarted } from '@features/analysisProgress/model/slice';
import type { AuditFinding, AuditScope, AuditSession } from '@entities/legal/types';

const Section = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};

  & > h4 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.55;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Steps = styled.ol`
  margin: 0 0 ${({ theme }) => theme.spacing(3)} 0;
  padding-left: ${({ theme }) => theme.spacing(5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;

  & > li {
    margin-bottom: 4px;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 240px;
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
  font-family: ${({ theme }) => theme.font.mono};
`;

const FindingCard = styled.div<{ $selected: boolean }>`
  border: 1px solid
    ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.primarySoft : theme.colors.backgroundElevated};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  cursor: pointer;
  transition:
    border-color 160ms ease,
    background 160ms ease;
`;

const VerdictBadge = styled.span<{ $verdict: string }>`
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme, $verdict }) =>
    $verdict === 'agent_missed' || $verdict === 'agent_wrong' || $verdict === 'agent_underestimate'
      ? theme.colors.status.danger
      : $verdict === 'agent_overestimate'
        ? theme.colors.status.warning
        : theme.colors.status.success};
  background: ${({ theme, $verdict }) =>
    ($verdict === 'agent_missed' || $verdict === 'agent_wrong' || $verdict === 'agent_underestimate'
      ? theme.colors.status.danger
      : $verdict === 'agent_overestimate'
        ? theme.colors.status.warning
        : theme.colors.status.success) + '22'};
`;

const Summary = styled.div`
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  line-height: 1.55;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
`;

const ProgressBar = styled.div`
  position: relative;
  height: 6px;
  border-radius: 999px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  margin-top: ${({ theme }) => theme.spacing(2)};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${({ theme }) => theme.colors.primary} 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.6s linear infinite;
  }
`;

const ParsingBox = styled.div`
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.primary}33;
  background: ${({ theme }) => theme.colors.primarySoft};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ErrorBox = styled.div`
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.status.danger};
  background: ${({ theme }) => theme.colors.status.danger}1A;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.status.danger};
`;

/** Сегмент-переключатель режима (аудит / эталонный документ). Виден только
 *  для composed-документов (allowReference). Договор/претензия эталон не
 *  поддерживают — для них переключателя нет, поведение прежнее. */
const ModeSwitch = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 2px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
`;

const ModeButton = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.background : theme.colors.text.secondary};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme, $active }) =>
      $active ? theme.colors.background : theme.colors.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** Галочка «Сразу перезапустить анализ договора» над кнопкой сохранения. */
const RerunOption = styled.label<{ $disabled: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  & > input {
    margin-top: 3px;
  }

  & small {
    display: block;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

/** Бейдж режима сессии в блоке активного парсинга/результата. */
const KindBadge = styled.span<{ $kind: 'audit' | 'reference' }>`
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme, $kind }) =>
    $kind === 'reference' ? theme.colors.status.info : theme.colors.text.secondary};
  background: ${({ theme, $kind }) =>
    $kind === 'reference' ? `${theme.colors.status.info}1A` : theme.colors.surface};
  border: 1px solid
    ${({ theme, $kind }) =>
      $kind === 'reference' ? `${theme.colors.status.info}33` : theme.colors.border};
`;

// Вердикт внешнего аудитора о решении нашего агента.
// Без латиницы: юрист видит однозначное русское описание ошибки агента.
const VERDICT_LABELS: Record<string, string> = {
  agent_missed: 'Агент пропустил',
  agent_wrong: 'Ошибочная позиция',
  agent_overestimate: 'Завышена важность',
  agent_underestimate: 'Занижена важность',
  agent_correct: 'Верно',
};

// Русские названия уровней важности — для отображения corrected_severity
// от внешнего аудитора. Сами значения в БД остаются critical/major/minor
// (так удобнее для машин и совместимо с существующей схемой), но
// пользователю показываем по-человечески.
const SEVERITY_LABELS: Record<string, string> = {
  critical: 'критический',
  major: 'важный',
  minor: 'незначительный',
};

/** Короткая русская подпись режима сессии для бейджа. */
const KIND_LABELS: Record<'audit' | 'reference', string> = {
  audit: 'аудит',
  reference: 'эталон',
};

interface Props {
  open: boolean;
  onClose: () => void;
  scope: AuditScope;
  entityId: string;
  /** Заголовок: «Договор / Претензия / Иск / тип документа». */
  entityLabel: string;
  /** Разрешить режим «эталонный документ» (показывает переключатель
   *  «Аудит» | «Эталонный документ»). Включается только для
   *  сгенерированных DOCX — у них есть с чем сравнивать эталон. Для
   *  договоров/претензий не передаётся → переключателя нет. */
  allowReference?: boolean;
  /** Статус договора (только для scope='contract'): пока анализ уже идёт,
   *  галочка «Сразу перезапустить анализ» недоступна. */
  contractStatus?: string;
}

/**
 * Универсальная модалка внешнего аудита: договор, претензия или иск.
 *
 * Async-flow:
 * 1. Юрист копирует пакет (clipboard, синхронно после клика — Firefox-friendly).
 * 2. Идёт во внешний ИИ, вставляет, получает ответ.
 * 3. Вставляет ответ в textarea, нажимает «Распарсить» → backend создаёт
 *    audit_session со status='parsing' и ставит Job в очередь, возвращает
 *    session_id мгновенно (без таймаута nginx).
 * 4. Frontend подписывается на WS-событие audit_session_updated через
 *    общий список useListAuditSessionsQuery (инвалидируется по тегу
 *    AuditSession:{scope}:{entity_id}).
 * 5. Когда session.status='ready' — показываются findings, юрист выбирает,
 *    сохраняет → confirm endpoint пишет в agent_learnings.
 *
 * Закрытие модалки НЕ отменяет парсинг — сессия живёт в БД. При повторном
 * открытии увидим её обратно через listAuditSessions: idле-парсинг → progress;
 * ready → findings; failed → error box.
 */
/** Куда реально уходят подтверждённые уроки (genitive, «на будущих анализах …»).
 * Для сгенерированных документов backend маршрутизирует уроки в контур анализа
 * РОДИТЕЛЬСКОЙ сущности (протокол → договор, ответ/претензия/иск → претензия):
 * документ компонуется из уже выявленных рисков, поэтому аудит документа — это
 * аудит анализа. Текст подсказки обязан говорить правду о назначении уроков. */
function lessonsTargetLabel(scope: AuditScope): string {
  switch (scope) {
    case 'contract':
    case 'protocol_disagreement':
      return 'договоров и их рисков';
    case 'claim':
    case 'response_claim':
    case 'outgoing_claim':
    case 'lawsuit':
      return 'претензий и их рисков';
  }
}

export function ExternalAuditModal({
  open,
  onClose,
  scope,
  entityId,
  entityLabel,
  allowReference = false,
  contractStatus,
}: Props) {
  // Пакет грузим сразу при открытии — clipboard.writeText синхронно
  // в обработчике клика (Firefox user-gesture activation).
  const packageQuery = useGetAuditPackageQuery({ scope, id: entityId }, { skip: !open });
  // Сессии: WS-bridge инвалидирует AuditSession:{scope}:{id} → автоматический rfetch.
  const sessionsQuery = useListAuditSessionsQuery(
    { scope, id: entityId },
    { skip: !open, refetchOnMountOrArgChange: true },
  );
  const [startSession, { isLoading: isStarting }] = useStartAuditSessionMutation();
  const [confirmSession, { isLoading: isConfirming }] = useConfirmAuditSessionMutation();
  const [analyzeContract] = useAnalyzeContractMutation();
  const dispatch = useAppDispatch();
  // Анализ договора уже идёт (статус или локальный прогресс, как у кнопки
  // «Перезапустить анализ» в версиях) — второй запуск не предлагаем.
  const hasLocalProgress = useAppSelector(
    (s) => scope === 'contract' && !!s.analysisProgress.byContract[entityId],
  );
  const contractAnalysisBusy =
    contractStatus === 'under_review' || contractStatus === 'parsing' || hasLocalProgress;

  const [text, setText] = useState('');
  const [sourceModel, setSourceModel] = useState('gpt-5');
  // Режим вставки: аудит (замечания) либо эталонный документ для сравнения.
  // reference доступен только при allowReference; default — audit.
  const [mode, setMode] = useState<'audit' | 'reference'>('audit');
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const [showFallback, setShowFallback] = useState(false);
  // Договор: после сохранения сразу перезапустить анализ, чтобы замечания
  // аудита применились к этому же договору. По умолчанию выключено —
  // повторный анализ заново пересчитывает риски текущей версии.
  const [rerunAfterSave, setRerunAfterSave] = useState(false);
  const [rerunStatus, setRerunStatus] = useState<'idle' | 'started' | 'error'>('idle');

  // Сброс UI-состояния при закрытии. Сама сессия в БД остаётся —
  // следующее открытие восстановит её через sessionsQuery.
  useEffect(() => {
    if (!open) {
      setText('');
      setMode('audit');
      setActiveSessionId(null);
      setSelected(new Set());
      setCopyStatus('idle');
      setShowFallback(false);
      setRerunAfterSave(false);
      setRerunStatus('idle');
    }
  }, [open]);

  // Активная сессия для отображения: либо явно выбранная после клика
  // «Распарсить», либо последняя по дате если есть незавершённый/готовый
  // парсинг этого entity.
  const activeSession: AuditSession | null = useMemo(() => {
    const items = sessionsQuery.data?.items ?? [];
    if (activeSessionId) {
      return items.find((s) => s.id === activeSessionId) ?? null;
    }
    // Если только что открыли модалку и есть последняя сессия в работе/ready,
    // которая ещё не была подтверждена — показываем её, чтобы юрист увидел
    // прогресс или результат без повторной вставки текста.
    return items.find((s) => s.status === 'parsing' || s.status === 'ready') ?? null;
  }, [sessionsQuery.data, activeSessionId]);

  // Когда активная сессия становится 'ready' — пред-выбираем все findings.
  useEffect(() => {
    if (
      activeSession?.status === 'ready' &&
      activeSession.parsed_findings &&
      selected.size === 0
    ) {
      setSelected(new Set(activeSession.parsed_findings.map((_, i) => i)));
    }
  }, [activeSession, selected.size]);

  // Идёт ли сейчас парсинг активной сессии — блокируем смену режима и ввод,
  // чтобы юрист не переключил «аудит ↔ эталон» посреди обработки и не запутался.
  const isParsing = activeSession?.status === 'parsing';
  // reference-режим показываем и применяем только когда он разрешён для scope.
  const isRef = allowReference && mode === 'reference';

  function onCopyPackage() {
    setCopyStatus('idle');
    setShowFallback(false);
    const pkg = packageQuery.data?.text;
    if (!pkg) {
      setCopyStatus('error');
      return;
    }
    const clipboard = navigator.clipboard;
    if (clipboard && typeof clipboard.writeText === 'function') {
      clipboard.writeText(pkg).then(
        () => {
          setCopyStatus('copied');
          setTimeout(() => setCopyStatus('idle'), 3000);
        },
        () => {
          setCopyStatus('error');
          setShowFallback(true);
        },
      );
    } else {
      setShowFallback(true);
    }
  }

  async function onStartParse() {
    if (!text.trim()) return;
    try {
      const session = await startSession({
        scope,
        id: entityId,
        text,
        source_model: sourceModel || 'unknown',
        // mode шлём только в режиме reference и только там, где он разрешён —
        // для договоров/претензий body остаётся прежним (без mode).
        ...(allowReference && mode === 'reference' ? { mode } : {}),
      }).unwrap();
      setActiveSessionId(session.id);
      setSelected(new Set());
      // Очищаем textarea чтобы не было соблазна повторно нажать.
      setText('');
    } catch {
      /* RTK покажет ошибку через isError, ничего критичного делать не надо */
    }
  }

  function toggle(idx: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  async function onConfirm() {
    if (!activeSession?.parsed_findings) return;
    const chosen = activeSession.parsed_findings.filter((_, i) => selected.has(i));
    if (chosen.length === 0) return;
    await confirmSession({
      sessionId: activeSession.id,
      scope,
      entityId,
      findings: chosen,
    }).unwrap();
    // Сессия станет 'confirmed' — держим её выбранной, иначе блок
    // «Аудит сохранён» пропадёт (без явного id показываются только parsing/ready).
    setActiveSessionId(activeSession.id);
    // Уроки к этому моменту уже в памяти агента (confirm ждёт embedding),
    // поэтому анализ, запущенный сразу после, их подхватит.
    if (scope === 'contract' && rerunAfterSave && !contractAnalysisBusy) {
      dispatch(analysisStarted({ contract_id: entityId }));
      try {
        await analyzeContract(entityId).unwrap();
        setRerunStatus('started');
      } catch {
        setRerunStatus('error');
      }
    }
  }

  return (
    <Modal open={open} title={`Внешний аудит — ${entityLabel}`} onClose={onClose}>
      {allowReference && (
        <ModeSwitch role="group" aria-label="Режим внешней проверки">
          <ModeButton
            type="button"
            $active={!isRef}
            aria-pressed={!isRef}
            disabled={isParsing}
            onClick={() => setMode('audit')}
          >
            Аудит
          </ModeButton>
          <ModeButton
            type="button"
            $active={isRef}
            aria-pressed={isRef}
            disabled={isParsing}
            onClick={() => setMode('reference')}
          >
            Эталонный документ
          </ModeButton>
        </ModeSwitch>
      )}

      <Hint>
        {isRef
          ? 'Скопируйте пакет, попросите внешний ИИ составить эталонный документ по той же сделке, вставьте результат сюда. Парсер сравнит эталон с нашим документом и выдаст расхождения. Сравнение идёт в фоне — можно закрыть модалку, при повторном открытии вы увидите прогресс или готовые расхождения.'
          : 'Скопируйте пакет (текст документа + наш анализ), вставьте во внешний ИИ (ChatGPT, Claude, Gemini) с запросом аудита, верните результат сюда. Парсинг идёт в фоне — можно закрыть модалку, при повторном открытии вы увидите прогресс или готовые findings.'}
      </Hint>

      <Section>
        <h4>1. Скопировать пакет для аудита</h4>
        {isRef ? (
          <Hint>
            Скопируйте пакет и попросите внешний ИИ составить правильный документ
            по этим материалам.
          </Hint>
        ) : (
          <Steps>
            <li>Нажмите кнопку ниже — пакет попадёт в буфер обмена.</li>
            <li>Откройте ChatGPT/Claude/Gemini, вставьте пакет в чат.</li>
            <li>Дождитесь развёрнутого аудита от ИИ.</li>
            <li>Скопируйте ответ, вернитесь сюда, вставьте в шаг 3.</li>
          </Steps>
        )}
        <Button type="button" onClick={onCopyPackage} disabled={!packageQuery.data}>
          {packageQuery.isFetching
            ? 'Готовим…'
            : copyStatus === 'copied'
              ? '✓ Скопировано'
              : 'Скопировать пакет'}
        </Button>
        {copyStatus === 'error' && !showFallback && (
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--osnova-danger, #d04848)' }}>
            Браузер заблокировал автокопирование. Откройте ручной режим.
          </div>
        )}
        {showFallback && packageQuery.data?.text && (
          <div style={{ marginTop: 10 }}>
            <Hint>
              Автокопирование заблокировано браузером. Выделите текст ниже (Ctrl+A
              в поле) и скопируйте вручную (Ctrl+C).
            </Hint>
            <TextArea
              readOnly
              value={packageQuery.data.text}
              onFocus={(e) => e.currentTarget.select()}
              style={{ minHeight: 160 }}
            />
          </div>
        )}
      </Section>

      <Section>
        <h4>{isRef ? '2. Какая модель составила эталон' : '2. Какая модель сделала аудит'}</h4>
        <Field>
          <Label htmlFor="src-model">Идентификатор модели</Label>
          <Input
            id="src-model"
            value={sourceModel}
            onChange={(e) => setSourceModel(e.target.value)}
            placeholder="напр., gpt-5, claude-opus-4-7, gemini-2.0-pro"
          />
        </Field>
      </Section>

      <Section>
        <h4>
          {isRef
            ? 'Вставьте текст эталонного документа, составленного другой нейронкой по этой же сделке'
            : '3. Вставьте текст аудита'}
        </h4>
        <TextArea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            isRef
              ? 'Вставьте сюда эталонный документ от внешнего ИИ. Парсер сравнит его с нашим документом и выдаст расхождения.'
              : 'Вставьте сюда ответ внешнего ИИ — в любой форме. Парсер на нашей стороне разберёт автоматически.'
          }
          disabled={isParsing}
        />
        <div style={{ marginTop: 8 }}>
          <Button
            type="button"
            onClick={onStartParse}
            disabled={!text.trim() || isStarting || isParsing}
          >
            {isStarting
              ? 'Отправляем…'
              : isRef
                ? 'Сравнить с эталоном'
                : 'Распарсить аудит'}
          </Button>
        </div>
      </Section>

      {activeSession?.status === 'parsing' && (
        <Section>
          <h4>Идёт парсинг…</h4>
          <ParsingBox>
            <strong>
              {(activeSession.kind ?? 'audit') === 'reference'
                ? 'Эталонный документ сравнивается фоном.'
                : 'Аудит обрабатывается фоном.'}
            </strong>{' '}
            Можно закрыть модалку — обработка продолжится. При повторном открытии вы
            увидите результат.
            <ProgressBar />
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                opacity: 0.7,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <KindBadge $kind={activeSession.kind ?? 'audit'}>
                {KIND_LABELS[activeSession.kind ?? 'audit']}
              </KindBadge>
              <span>
                Модель: {activeSession.source_model} · сессия #{activeSession.id.slice(0, 8)}
              </span>
            </div>
          </ParsingBox>
        </Section>
      )}

      {activeSession?.status === 'failed' && (
        <Section>
          <h4>Ошибка парсинга</h4>
          <ErrorBox>
            <strong>Не удалось обработать аудит.</strong>{' '}
            {activeSession.error_message ?? 'Подробности в логах сервера.'}
          </ErrorBox>
          <div style={{ marginTop: 8 }}>
            <Hint>Можете повторить попытку: вставьте текст в шаг 3 и нажмите «Распарсить» снова.</Hint>
          </div>
        </Section>
      )}

      {activeSession?.status === 'ready' && activeSession.parsed_findings && (
        <Section>
          <h4 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>4. Выберите что добавить в память агента</span>
            <KindBadge $kind={activeSession.kind ?? 'audit'}>
              {KIND_LABELS[activeSession.kind ?? 'audit']}
            </KindBadge>
          </h4>
          {activeSession.parsed_summary && <Summary>{activeSession.parsed_summary}</Summary>}
          {activeSession.parsed_findings.length === 0 ? (
            <Hint>Парсер не нашёл findings в этом тексте.</Hint>
          ) : (
            <>
              <Hint>
                Снимите галочку с findings, которые не считаете полезными. Принятые
                findings агент учтёт на будущих анализах {lessonsTargetLabel(scope)}.
              </Hint>
              {activeSession.parsed_findings.map((f: AuditFinding, i: number) => (
                <FindingCard key={i} $selected={selected.has(i)} onClick={() => toggle(i)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <input
                      type="checkbox"
                      checked={selected.has(i)}
                      onChange={() => toggle(i)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <VerdictBadge $verdict={f.verdict}>
                      {VERDICT_LABELS[f.verdict] ?? f.verdict}
                    </VerdictBadge>
                    {f.corrected_severity && (
                      <span style={{ fontSize: 11, color: 'var(--osnova-text-muted, #888)' }}>
                        → важность:{' '}
                        <strong>
                          {SEVERITY_LABELS[f.corrected_severity] ?? f.corrected_severity}
                        </strong>
                      </span>
                    )}
                    {f.related_risk_id && (
                      <span style={{ fontSize: 11, color: 'var(--osnova-text-muted, #888)' }}>
                        · связан с риском {f.related_risk_id.slice(0, 8)}…
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.55 }}>{f.finding}</div>
                  {f.corrected_assessment && (
                    <div
                      style={{
                        marginTop: 6,
                        paddingTop: 6,
                        borderTop: '1px dashed var(--osnova-border, #555)',
                        fontSize: 12,
                        color: 'var(--osnova-text-secondary, #aaa)',
                      }}
                    >
                      <strong>Должно быть:</strong> {f.corrected_assessment}
                    </div>
                  )}
                  {f.tags && f.tags.length > 0 && (
                    <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {f.tags.map((t, j) => (
                        <span
                          key={j}
                          style={{
                            fontSize: 10,
                            padding: '2px 6px',
                            background: 'var(--osnova-surface, #2a2a2a)',
                            borderRadius: 999,
                            color: 'var(--osnova-text-muted, #888)',
                          }}
                        >
                          #{t}
                        </span>
                      ))}
                    </div>
                  )}
                </FindingCard>
              ))}
              {scope === 'contract' && (
                <RerunOption $disabled={contractAnalysisBusy}>
                  <input
                    type="checkbox"
                    checked={rerunAfterSave && !contractAnalysisBusy}
                    disabled={contractAnalysisBusy || isConfirming}
                    onChange={(e) => setRerunAfterSave(e.target.checked)}
                  />
                  <span>
                    Сразу перезапустить анализ договора
                    <small>
                      {contractAnalysisBusy
                        ? 'Анализ договора уже идёт — замечания учтутся на следующем запуске.'
                        : 'Риски текущей версии будут пересчитаны заново с учётом этих замечаний — так же, как по кнопке «Перезапустить анализ».'}
                    </small>
                  </span>
                </RerunOption>
              )}
              <Button
                type="button"
                onClick={onConfirm}
                disabled={isConfirming || selected.size === 0}
                style={{ marginTop: 8 }}
              >
                {isConfirming ? 'Сохраняем…' : `Сохранить ${selected.size} в память агента`}
              </Button>
            </>
          )}
        </Section>
      )}

      {activeSession?.status === 'confirmed' && (
        <Section>
          <h4>Аудит сохранён</h4>
          <div
            style={{
              padding: 12,
              background: 'var(--osnova-success-soft, #1a3a1a)',
              border: '1px solid var(--osnova-success, #15803d)',
              borderRadius: 8,
              color: 'var(--osnova-success, #15803d)',
              fontSize: 13,
            }}
          >
            ✓ Сохранено {activeSession.confirmed_count} findings в памяти агента.{' '}
            {rerunStatus === 'started'
              ? 'Анализ договора перезапущен с учётом этих замечаний — прогресс виден в карточке договора.'
              : `На следующих анализах ${lessonsTargetLabel(scope)} эти замечания будут учтены. Чтобы применить аудит к этому документу сразу, закройте модалку и нажмите «Перезапустить анализ».`}
          </div>
          {rerunStatus === 'error' && (
            <ErrorBox style={{ marginTop: 8 }}>
              Замечания сохранены, но перезапустить анализ не удалось. Закройте модалку и нажмите
              «Перезапустить анализ» в карточке договора.
            </ErrorBox>
          )}
        </Section>
      )}
    </Modal>
  );
}
